import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Info, AlertTriangle, XCircle, CheckCircle2 } from "lucide-react";

/* ── Typography ──────────────────────────────────────────────────── */

const headingClasses: Record<number, string> = {
  1: "text-3xl font-display font-bold mt-10 mb-4",
  2: "text-2xl font-display font-bold mt-10 mb-4",
  3: "text-xl font-semibold mt-8 mb-3",
  4: "text-lg font-semibold mt-6 mb-2",
  5: "text-base font-semibold mt-4 mb-2",
  6: "text-sm font-semibold mt-4 mb-1",
};

export function Heading({ level, children, id }: { level: number; children: React.ReactNode; id?: string }) {
  const Tag = `h${level}` as keyof JSX.IntrinsicElements;
  return <Tag id={id} className={headingClasses[level] || ""}>{children}</Tag>;
}

export function Paragraph({ children }: { children: React.ReactNode }) {
  return <p className="text-muted-foreground leading-relaxed my-3">{children}</p>;
}

export function Strong({ children }: { children: React.ReactNode }) {
  return <strong className="font-semibold text-foreground">{children}</strong>;
}

export function Emphasis({ children }: { children: React.ReactNode }) {
  return <em>{children}</em>;
}

export function Strikethrough({ children }: { children: React.ReactNode }) {
  return <del>{children}</del>;
}

/* ── Code ─────────────────────────────────────────────────────── */

export function InlineCode({ children }: { children: React.ReactNode }) {
  return (
    <code className="px-1.5 py-0.5 rounded bg-muted text-sm font-mono">
      {children}
    </code>
  );
}

export function CodeBlock({ children, content, language }: { children: React.ReactNode; content?: string; language?: string }) {
  return (
    <pre className="p-4 rounded-lg bg-muted overflow-x-auto my-4">
      <code className="text-sm font-mono" data-lang={language}>
        {content || children}
      </code>
    </pre>
  );
}

/* ── Links & Images ──────────────────────────────────────────── */

export function MarkdocLink({ href, children }: { href: string; children: React.ReactNode }) {
  if (href.startsWith("/")) {
    return (
      <Link to={href} className="text-primary hover:underline">
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

export function MarkdocImage({ src, alt }: { src: string; alt?: string }) {
  return (
    <figure className="my-6">
      <img src={src} alt={alt || ""} className="rounded-lg max-w-full mx-auto" loading="lazy" />
      {alt && <figcaption className="text-center text-sm text-muted-foreground mt-2">{alt}</figcaption>}
    </figure>
  );
}

/* ── Block Elements ──────────────────────────────────────────── */

export function Blockquote({ children }: { children: React.ReactNode }) {
  return (
    <blockquote className="border-l-4 border-primary/40 pl-4 py-2 my-4 text-muted-foreground italic bg-muted/30 rounded-r-lg">
      {children}
    </blockquote>
  );
}

export function HorizontalRule() {
  return <hr className="my-8 border-border" />;
}

export function List({ ordered, children }: { ordered?: boolean; children: React.ReactNode }) {
  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag className={`ml-6 my-3 ${ordered ? "list-decimal" : "list-disc"} text-muted-foreground space-y-1`}>
      {children}
    </Tag>
  );
}

export function ListItem({ children }: { children: React.ReactNode }) {
  return <li className="my-1">{children}</li>;
}

/* ── Table ────────────────────────────────────────────────────── */

export function Table({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto my-4">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  );
}

export function TableHead({ children }: { children: React.ReactNode }) {
  return <thead>{children}</thead>;
}

export function TableBody({ children }: { children: React.ReactNode }) {
  return <tbody>{children}</tbody>;
}

export function TableRow({ children }: { children: React.ReactNode }) {
  return <tr>{children}</tr>;
}

export function TableHeader({ children }: { children: React.ReactNode }) {
  return (
    <th className="border border-border p-2 bg-muted text-left font-semibold">
      {children}
    </th>
  );
}

export function TableCell({ children }: { children: React.ReactNode }) {
  return <td className="border border-border p-2">{children}</td>;
}

/* ── Custom Tags ──────────────────────────────────────────────── */

const calloutStyles = {
  info: { icon: Info, border: "border-primary/40", bg: "bg-primary/5", iconColor: "text-primary" },
  warning: { icon: AlertTriangle, border: "border-reqqy-warning/40", bg: "bg-reqqy-warning/5", iconColor: "text-reqqy-warning" },
  error: { icon: XCircle, border: "border-destructive/40", bg: "bg-destructive/5", iconColor: "text-destructive" },
  success: { icon: CheckCircle2, border: "border-reqqy-success/40", bg: "bg-reqqy-success/5", iconColor: "text-reqqy-success" },
};

export function Callout({ type = "info", title, children }: { type?: string; title?: string; children: React.ReactNode }) {
  const style = calloutStyles[type as keyof typeof calloutStyles] || calloutStyles.info;
  const Icon = style.icon;
  return (
    <div className={`border-l-4 ${style.border} ${style.bg} rounded-r-lg p-4 my-4`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${style.iconColor}`} />
        <div className="flex-1">
          {title && <p className="font-semibold mb-1">{title}</p>}
          <div className="text-sm text-muted-foreground">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function Tabs({ children }: { children: React.ReactNode }) {
  const tabs = React.Children.toArray(children) as React.ReactElement[];
  const [active, setActive] = useState(0);

  return (
    <div className="my-4">
      <div className="flex border-b border-border mb-4 gap-1">
        {tabs.map((tab, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2 ${
              i === active
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.props.label}
          </button>
        ))}
      </div>
      {tabs[active]}
    </div>
  );
}

export function Tab({ children }: { label: string; children: React.ReactNode }) {
  return <div>{children}</div>;
}

export function MarkdocBadge({ text, variant = "default" }: { text: string; variant?: "default" | "secondary" | "destructive" | "outline" }) {
  return <Badge variant={variant} className="text-xs">{text}</Badge>;
}
